/**
 * share.js — Screenshot + watermark + Web Share API
 *
 * Captures the Three.js canvas, overlays a DueProof watermark,
 * then invokes navigator.share or falls back to download.
 *
 * The Three.js renderer must be created with preserveDrawingBuffer: true.
 */

import { renderer } from './scene.js'
import { t } from './i18n/index.js'

/**
 * Capture the current frame and share it.
 * @param {{ level: number, label: string }} islandInfo
 */
export async function shareScreenshot(islandInfo) {
  try {
    // 1. Force a render to ensure canvas is up to date
    //    (preserveDrawingBuffer keeps the last frame)
    const srcCanvas = renderer.domElement

    // 2. Create an offscreen canvas for compositing
    const out = document.createElement('canvas')
    out.width = srcCanvas.width
    out.height = srcCanvas.height
    const ctx = out.getContext('2d')

    // Draw Three.js frame
    ctx.drawImage(srcCanvas, 0, 0)

    // 3. Overlay watermark
    _drawWatermark(ctx, out.width, out.height, islandInfo)

    // 4. Export as blob
    const blob = await new Promise((resolve) => out.toBlob(resolve, 'image/png'))
    if (!blob) throw new Error('Canvas toBlob failed')

    // 5. Share or download
    if (
      navigator.share &&
      navigator.canShare?.({ files: [new File([blob], 'dueproof.png', { type: 'image/png' })] })
    ) {
      const file = new File([blob], `dueproof_lv${islandInfo.level}.png`, { type: 'image/png' })
      await navigator.share({
        title: t('game.share_title', '我的時光星球 Lv.{level}', { level: islandInfo.level }),
        text: t('game.share_text', '在 DueProof 記錄時光，我的星球已進化到「{label}」階段！', {
          label: islandInfo.label,
        }),
        files: [file],
      })
    } else {
      // Fallback: trigger download
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `dueproof_planet_lv${islandInfo.level}.png`
      a.click()
      setTimeout(() => URL.revokeObjectURL(url), 5000)
    }
  } catch (e) {
    if (e.name !== 'AbortError') {
      console.error('[share] Failed:', e.message)
    }
  }
}

// ── Watermark drawing ─────────────────────────────────────────────────────────
function _drawWatermark(ctx, w, h) {
  // Semi-transparent bottom strip
  const stripH = Math.round(h * 0.12)
  const grad = ctx.createLinearGradient(0, h - stripH, 0, h)
  grad.addColorStop(0, 'rgba(10,10,26,0)')
  grad.addColorStop(1, 'rgba(10,10,26,0.85)')
  ctx.fillStyle = grad
  ctx.fillRect(0, h - stripH, w, stripH)

  // Logo text
  const fontSize = Math.round(w * 0.038)
  ctx.font = `600 ${fontSize}px -apple-system, BlinkMacSystemFont, sans-serif`
  ctx.fillStyle = 'rgba(255,255,255,0.9)'
  ctx.textAlign = 'left'
  ctx.fillText('✦ DueProof', Math.round(w * 0.05), h - Math.round(h * 0.04))

  // Sub-text
  const subSize = Math.round(w * 0.026)
  ctx.font = `400 ${subSize}px -apple-system, BlinkMacSystemFont, sans-serif`
  ctx.fillStyle = 'rgba(255,255,255,0.5)'
  ctx.fillText(
    t('game.tagline', '記錄每一個成長的瞬間'),
    Math.round(w * 0.05),
    h - Math.round(h * 0.015),
  )
}
